import React, { useState } from 'react';
import Sidebar from './Sidebar';
import { Outlet, useLocation } from 'react-router-dom';

const SCREEN_TITLES: Record<string, string> = {
    '/dashboard': 'Visão Geral',
    '/dashboard/agenda': 'Agenda',
    '/dashboard/clients': 'Clientes',
    '/dashboard/services': 'Serviços',
    '/dashboard/financial': 'Financeiro',
    '/dashboard/marketing': 'Marketing',
    '/dashboard/reports': 'Relatórios',
    '/dashboard/membership': 'Clube de Assinaturas',
    '/dashboard/subscription': 'Meu Plano',
    '/dashboard/settings': 'Configurações',
    '/dashboard/products': 'Produtos',
    '/dashboard/reviews': 'Avaliações',
};

interface DashboardLayoutProps {
    children?: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
    const { pathname } = useLocation();
    const title = SCREEN_TITLES[pathname] || 'HoraMarcada Pro';

    const [isMobileOpen, setIsMobileOpen] = useState(false);

    // Dark Mode Logic
    const [theme, setTheme] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('theme') || 'light';
        }
        return 'light';
    });

    React.useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    return (
        <div className="flex h-screen bg-background-light dark:bg-background-dark outline-none">
            {/* Sidebar - hidden on print */}
            <div className="print:hidden">
                <Sidebar
                    isMobileOpen={isMobileOpen}
                    onMobileClose={() => setIsMobileOpen(false)}
                    currentTheme={theme}
                    onThemeToggle={toggleTheme}
                />
            </div>

            <div className="flex flex-1 flex-col overflow-hidden">
                {/* Topbar - hidden on print */}
                <header className="flex h-16 items-center justify-between border-b border-[#d2e5dd] dark:border-[#2a4035] bg-white/80 dark:bg-surface-dark/80 px-6 backdrop-blur-md print:hidden">
                    <div className="flex items-center gap-4">
                        <button
                            className="lg:hidden p-1 -ml-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                            onClick={() => setIsMobileOpen(true)}
                        >
                            <span className="material-symbols-outlined">menu</span>
                        </button>
                        <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                            {title || 'HoraMarcada Pro'}
                        </h1>
                    </div>

                    <div className="flex items-center gap-4">
                        <button className="flex size-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors">
                            <span className="material-symbols-outlined text-[24px]">notifications</span>
                        </button>
                        <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
                            ES
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <main className="flex-1 overflow-y-auto p-4 lg:p-8 print:p-0 print:overflow-visible">
                    <div className="mx-auto max-w-6xl print:max-w-none print:mx-0">
                        {children || <Outlet />}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default DashboardLayout;
